/**
 * The volunteer device test's report (ADR 0028 amendment, 2026-09-28): the
 * view's numbers plus what the browser says about the device, for the stats
 * panel (`?stats=1`) and its "copy report" button.
 */
import type { ViewStats } from '@ab/renderer';
import type { StatsReport } from '@ab/ui';

/** The panel summarises the last 120 frames of each series. */
const recent = 120;

const p90 = (samples: readonly number[]): number | null => {
  const sorted = samples.slice(-recent).sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length * 0.9)] ?? null;
};

type MemoryInfo = { readonly usedJSHeapSize: number };
/** `performance.memory` exists in Chromium browsers only. */
const heapMb = (p: Performance): number | null => {
  const memory: unknown = (p as Performance & { readonly memory?: unknown }).memory;
  if (typeof memory !== 'object' || memory === null || typeof (memory as Partial<MemoryInfo>).usedJSHeapSize !== 'number') return null;
  return (memory as MemoryInfo).usedJSHeapSize / (1024 * 1024);
};

/**
 * The report of a view (the dungeon's or a scene's: anything with `stats`); a scene's carries its id. Since slice 6b
 * it also says the page's locale, the visual viewport's size (CSS pixels; the window's where there is none) and
 * whether the touch pad shows; a scene's says its time of day too (`time`, ADR 0032: the night run of the volunteer
 * recipe).
 */
export const statsReport = (view: { stats(): ViewStats }, page: { readonly locale: string; readonly pad: boolean; readonly time?: string }): StatsReport => {
  const s = view.stats();
  const v = window.visualViewport;
  return {
    tier: s.tier,
    pixelRatio: s.pixelRatio,
    canvas: [s.canvasWidth, s.canvasHeight],
    drawCalls: s.drawCalls,
    triangles: s.triangles,
    cpuFrameP90Ms: p90(s.cpuFrameSamples),
    gpuFrameP90Ms: p90(s.gpuSamples),
    animationFrameP90Ms: p90(s.animationFrameSamples),
    heapMb: heapMb(performance),
    devicePixelRatio: window.devicePixelRatio,
    userAgent: navigator.userAgent,
    locale: page.locale,
    viewport: [Math.round(v?.width ?? window.innerWidth), Math.round(v?.height ?? window.innerHeight)],
    pad: page.pad,
    ...(s.scene?.id == null ? {} : { scene: s.scene.id }),
    ...(page.time === undefined ? {} : { time: page.time }),
  };
};
